/**
 * Calendly inline-embed helpers.
 *
 * The booking app embeds Calendly through `react-calendly`. This repo does not carry
 * that dependency, and the widget is a plain iframe underneath, so we compose the URL
 * ourselves. The query params below are the ones react-calendly's InlineWidget sets.
 */

export const CALENDLY_ORIGIN = 'https://calendly.com'

export type CalendlyPrefill = {
  name?: string
  email?: string
  /** Calendly's opt-in SMS reminder field — the `text_reminder_number` param. */
  smsReminderNumber?: string
  /** Answers to the event type's custom questions, keyed `a1`, `a2`, … */
  customAnswers?: Record<string, string | undefined>
}

/**
 * Builds the iframe src for an inline embed.
 *
 * `embed_type` and `embed_domain` are not decoration: without them Calendly does not
 * post its events back to the host page, so `calendly.event_scheduled` — the message
 * that triggers our Slack notification — never arrives.
 */
export const buildCalendlyUrl = (baseUrl: string, prefill: CalendlyPrefill = {}): string => {
  let url: URL

  try {
    url = new URL(baseUrl)
  } catch (error) {
    return baseUrl
  }

  const params = url.searchParams

  if (prefill.name) params.set('name', prefill.name)
  if (prefill.email) params.set('email', prefill.email)
  if (prefill.smsReminderNumber) params.set('text_reminder_number', prefill.smsReminderNumber)

  Object.entries(prefill.customAnswers ?? {}).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })

  params.set('embed_type', 'Inline')
  if (typeof window !== 'undefined') params.set('embed_domain', window.location.host)
  params.set('hide_gdpr_banner', '1')

  return url.toString()
}

/**
 * `/landing/calendly-scheduled` expects the trailing segment of the event URI
 * (`https://api.calendly.com/scheduled_events/<id>`), not the URI itself.
 */
export const eventIdFromUri = (uri?: string | null): string | null => {
  if (!uri) return null
  return uri.split('/').filter(Boolean).pop() ?? null
}
